export const BRAND_FONT_IS_VARIABLE = {
  Inter: false,
  Montserrat: true,
  "Nunito Sans": true,
  Poppins: false,
  Jost: true,
  Bitter: true,
  "Source Serif 4": true,
  "Bebas Neue": false,
  Lato: false,
  Oswald: true,
  Raleway: true,
  "Source Sans 3": true,
  "Rethink Sans": true,
  Newsreader: true,
  Mulish: true,
  "Roboto Condensed": true,
  "Crimson Pro": true,
  Lora: true,
  "DM Sans": true,
  Fraunces: true,
  "DM Serif Display": false,
  "Instrument Serif": false,
  Archivo: true,
  "Bricolage Grotesque": true,
} as const;

export type BrandFontFamily = keyof typeof BRAND_FONT_IS_VARIABLE;
export const BRAND_FONT_FAMILIES = Object.keys(
  BRAND_FONT_IS_VARIABLE
) as BrandFontFamily[];

const ALLOWED = new Set<string>(BRAND_FONT_FAMILIES);

/** Display faces set headings only: Theme's body picker and the look
 * normalizer leave them out, and a body slot never stores one. */
export const BRAND_DISPLAY_FACES: readonly BrandFontFamily[] = [
  "Fraunces",
  "DM Serif Display",
  "Instrument Serif",
  "Archivo",
  "Bricolage Grotesque",
];

const DISPLAY = new Set<string>(BRAND_DISPLAY_FACES);

export const isDisplayFace = (family: unknown): boolean =>
  typeof family === "string" && DISPLAY.has(family);

export const BRAND_SINGLE_WEIGHT_FACES: ReadonlySet<BrandFontFamily> = new Set([
  "Bebas Neue",
  "DM Serif Display",
  "Instrument Serif",
]);

export const BRAND_BODY_FONT_FAMILIES = BRAND_FONT_FAMILIES.filter(
  (family) => !DISPLAY.has(family)
);

export interface ResolvedBrandFonts {
  heading: BrandFontFamily | null;
  body: BrandFontFamily | null;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export const isBrandFontFamily = (value: unknown): value is BrandFontFamily =>
  typeof value === "string" && ALLOWED.has(value);

const allowlistedFamily = (value: unknown): BrandFontFamily | null =>
  isBrandFontFamily(value) ? value : null;

export const brandFontCssFamily = (family: BrandFontFamily): string =>
  BRAND_FONT_IS_VARIABLE[family] ? `${family} Variable` : family;

export const resolveBrandFonts = (
  theme: unknown
): ResolvedBrandFonts | null => {
  if (!isRecord(theme)) return null;
  if (!isRecord(theme.typography)) return null;
  if (!isRecord(theme.typography.brandFonts)) return null;

  const heading = allowlistedFamily(theme.typography.brandFonts.heading);
  const body = allowlistedFamily(theme.typography.brandFonts.body);
  if (!heading && !body) return null;
  return { heading, body };
};

export const brandFontCssVars = (
  fonts: ResolvedBrandFonts | null
): Record<string, string> | null => {
  if (!fonts) return null;
  const vars: Record<string, string> = {};
  if (fonts.heading) {
    vars["--font-brand-heading"] = `"${brandFontCssFamily(fonts.heading)}"`;
  }
  if (fonts.body) {
    vars["--font-brand-body"] = `"${brandFontCssFamily(fonts.body)}"`;
  }
  return Object.keys(vars).length ? vars : null;
};

export const brandFontRootCss = (vars: Record<string, string>): string =>
  `:root{${Object.entries(vars)
    .map(([key, value]) => `${key}:${value}`)
    .join(";")}}`;

export const brandFontFamiliesToLoad = (
  fonts: ResolvedBrandFonts | null
): BrandFontFamily[] => {
  if (!fonts) return [];
  const families = new Set<BrandFontFamily>();
  if (fonts.heading) families.add(fonts.heading);
  if (fonts.body) families.add(fonts.body);
  return [...families];
};

const slotFromSubmitted = (
  submitted: unknown,
  existing: unknown
): BrandFontFamily | undefined => {
  if (submitted === undefined) {
    return allowlistedFamily(existing) ?? undefined;
  }
  return allowlistedFamily(submitted) ?? undefined;
};

export const applyBrandFontsToTypography = (
  typography: unknown,
  heading: unknown,
  body: unknown
): Record<string, unknown> => {
  const next: Record<string, unknown> = isRecord(typography)
    ? { ...typography }
    : {};
  const existing = isRecord(next.brandFonts) ? next.brandFonts : {};
  const nextFonts: Record<string, BrandFontFamily> = {};
  const headingFamily = slotFromSubmitted(heading, existing.heading);
  const submittedBody = slotFromSubmitted(body, existing.body);
  const bodyFamily = isDisplayFace(submittedBody) ? undefined : submittedBody;
  if (headingFamily) nextFonts.heading = headingFamily;
  if (bodyFamily) nextFonts.body = bodyFamily;
  if (Object.keys(nextFonts).length) {
    next.brandFonts = nextFonts;
  } else {
    delete next.brandFonts;
  }
  return next;
};

export const brandFontStylesheetHrefs = (
  families: BrandFontFamily[],
  hrefsByFamily: Partial<Record<BrandFontFamily, readonly string[]>>
): string[] => {
  const hrefs: string[] = [];
  const seen = new Set<string>();
  for (const family of families) {
    for (const href of hrefsByFamily[family] ?? []) {
      if (!seen.has(href)) {
        seen.add(href);
        hrefs.push(href);
      }
    }
  }
  return hrefs;
};
